import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {AdapterMoment, Box, ButtonBase, DateCalendar, Dropdown, DropdownMenuItem, LocalizationProvider, Popover, Stack, Typography} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import FailureCategoriesSection from './sections/failure_categories/FailureCategoriesSection';
import MetricCardsSection from './sections/metric_cards/MetricCardsSection';
import TokenSpendSection from './sections/token_spend/TokenSpendSection';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const ALL_GUIDES_VALUE = 'ALL';

const emptyCounters = () => ({
  marked: 0,
  markedGood: 0,
  markedBad: 0,
  flagged: 0,
  byFailureCategory: {},
  autoFailCount: 0,
  interventionCount: 0,
  skippedIntervention: 0,
  humanRecords: 0,
  teacherHumanDisagreements: 0,
  teacherPassButIntervened: 0,
});

// Sums rollup totals over a set of metric dates; days without a rollup row
// count as zero.
const sumCountersForDates = (dates, metricsByDate) => {
  return dates.reduce((summedCounters, date) => {
    const dayCounters = metricsByDate[date]?.totals;

    if (!dayCounters) {
      return summedCounters;
    }

    Object.keys(summedCounters).forEach((counterKey) => {
      if (counterKey === 'byFailureCategory') {
        Object.entries(dayCounters.byFailureCategory || {}).forEach(([category, count]) => {
          summedCounters.byFailureCategory[category] = (summedCounters.byFailureCategory[category] || 0) + count;
        });
        return;
      }

      summedCounters[counterKey] += dayCounters[counterKey] || 0;
    });

    return summedCounters;
  }, emptyCounters());
};

const emptyTokenCounters = () => ({
  inputTokens: 0,
  outputTokens: 0,
  cacheReadInputTokens: 0,
  cacheCreationInputTokens: 0,
});

// Sums the endpoint's tokenSpend totals over a set of metric dates. Rollups
// written before token tracking carry no tokenSpend at all, so hasData only
// flips once a fetched day actually reports the field — the panel stays
// hidden against a pre-token engine.
const sumTokenSpendForDates = (dates, metricsByDate) => {
  return dates.reduce((summedSpend, date) => {
    const dayTokenSpend = metricsByDate[date]?.tokenSpend?.totals;

    if (!dayTokenSpend) {
      return summedSpend;
    }

    summedSpend.hasData = true;
    ['sweep', 'regression'].forEach((bucketKey) => {
      Object.keys(summedSpend[bucketKey]).forEach((counterKey) => {
        summedSpend[bucketKey][counterKey] += dayTokenSpend[bucketKey]?.[counterKey] || 0;
      });
    });

    return summedSpend;
  }, {hasData: false, sweep: emptyTokenCounters(), regression: emptyTokenCounters()});
};

const getFailRatePercentage = (counters) => {
  const markedWithGrade = counters.markedGood + counters.markedBad;

  if (markedWithGrade === 0) {
    return null;
  }

  return (counters.markedBad / markedWithGrade) * 100;
};

const formatRate = (rate) => {
  if (rate === null) {
    return '--';
  }

  return Math.abs(rate % 1) < 0.05 ? `${rate.toFixed(0)}%` : `${rate.toFixed(1)}%`;
};

const prettifyCategoryKey = (categoryKey) => {
  const label = `${categoryKey}`.replace(/_/g, ' ');

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const AgentReportCard = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  const timezone = 'America/Toronto';
  // The rollup runs nightly, so "today" is never available yet.
  const latestAvailableDate = React.useMemo(() => {
    return moment.tz(timezone).subtract(1, 'day').startOf('day');
  }, [timezone]);

  const [comparisonMode, setComparisonMode] = React.useState('WEEKLY');
  const [selectedCurrentDate, setSelectedCurrentDate] = React.useState(latestAvailableDate.format('YYYY-MM-DD'));
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [selectedGuideRn, setSelectedGuideRn] = React.useState(ALL_GUIDES_VALUE);
  const [domainGuides, setDomainGuides] = React.useState([]);
  const [metricsState, setMetricsState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    metricsByDate: {},
    fetchedDates: {},
  });

  const comparisonWindow = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone).startOf('day');
    const periodLength = comparisonMode === 'DAILY' ? 1 : 7;
    const currentPeriodDates = Array.from({length: periodLength}, (_, index) => {
      return currentDateMoment.clone().subtract(periodLength - 1 - index, 'day').format('YYYY-MM-DD');
    });
    const previousPeriodDates = Array.from({length: periodLength}, (_, index) => {
      return currentDateMoment.clone().subtract((periodLength * 2) - 1 - index, 'day').format('YYYY-MM-DD');
    });
    const graphDates = Array.from({length: 7}, (_, index) => {
      return currentDateMoment.clone().subtract(6 - index, 'day').format('YYYY-MM-DD');
    });

    return {
      currentPeriodDates,
      previousPeriodDates,
      graphDates,
    };
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const requestedDates = React.useMemo(() => {
    return Array.from(
        new Set([
          ...comparisonWindow.currentPeriodDates,
          ...comparisonWindow.previousPeriodDates,
          ...comparisonWindow.graphDates,
        ]),
    );
  }, [comparisonWindow]);

  React.useEffect(() => {
    let isActive = true;

    const loadDomainGuides = async () => {
      try {
        const domainGuideResponse = await backend.domainGuide.listDomainGuides();
        const domainGuideList = Array.isArray(domainGuideResponse) ? domainGuideResponse : domainGuideResponse?.list;

        if (isActive) {
          setDomainGuides(domainGuideList || []);
        }
      } catch {
        // The guide filter is optional; totals still load without it.
      }
    };

    loadDomainGuides();

    return () => {
      isActive = false;
    };
  }, []);

  // A guide change invalidates every cached date: the endpoint scopes totals
  // to the requested guide.
  React.useEffect(() => {
    setMetricsState({
      isLoading: true,
      hasLoadedOnce: false,
      errorMessage: '',
      metricsByDate: {},
      fetchedDates: {},
    });
  }, [selectedGuideRn]);

  React.useEffect(() => {
    let isActive = true;

    const loadMetrics = async () => {
      const datesToFetch = requestedDates.filter((date) => !metricsState.fetchedDates[date]);

      if (!datesToFetch.length) {
        return;
      }

      setMetricsState((currentState) => ({
        ...currentState,
        isLoading: true,
        errorMessage: '',
      }));

      await progressWrap(async () => {
        try {
          const guideRn = selectedGuideRn === ALL_GUIDES_VALUE ? undefined : selectedGuideRn;
          const metricsResponse = await backend.metrics.queryAgentReportCardMetrics(datesToFetch, guideRn);

          if (!isActive) {
            return;
          }

          setMetricsState((currentState) => {
            const metricsByDate = {
              ...currentState.metricsByDate,
            };

            (metricsResponse?.metrics || []).forEach((metric) => {
              metricsByDate[metric.metricDate] = metric;
            });

            return {
              ...currentState,
              isLoading: false,
              hasLoadedOnce: true,
              errorMessage: '',
              metricsByDate,
              fetchedDates: {
                ...currentState.fetchedDates,
                ...datesToFetch.reduce((nextFetchedDates, date) => {
                  nextFetchedDates[date] = true;

                  return nextFetchedDates;
                }, {}),
              },
            };
          });
        } catch (error) {
          if (!isActive) {
            return;
          }

          setMetricsState((currentState) => ({
            ...currentState,
            isLoading: false,
            errorMessage: error?.message || translate('metrics.AgentReportCard.LoadError'),
          }));
        }
      }, {showProgress: false});
    };

    loadMetrics();

    return () => {
      isActive = false;
    };
  }, [metricsState.fetchedDates, progressWrap, requestedDates, selectedGuideRn]);

  const currentCounters = React.useMemo(() => {
    return sumCountersForDates(comparisonWindow.currentPeriodDates, metricsState.metricsByDate);
  }, [comparisonWindow.currentPeriodDates, metricsState.metricsByDate]);
  const previousCounters = React.useMemo(() => {
    return sumCountersForDates(comparisonWindow.previousPeriodDates, metricsState.metricsByDate);
  }, [comparisonWindow.previousPeriodDates, metricsState.metricsByDate]);

  const buildCountCard = React.useCallback((label, counterKey, invertTrend) => {
    const currentCount = currentCounters[counterKey];
    const previousCount = previousCounters[counterKey];
    const delta = currentCount - previousCount;
    let deltaPercentage = 0;

    if (previousCount === 0 && currentCount > 0) {
      deltaPercentage = 100;
    } else if (previousCount > 0) {
      deltaPercentage = (delta / previousCount) * 100;
    }

    return {
      label,
      currentLabel: `${currentCount}`,
      previousLabel: `${previousCount}`,
      delta,
      deltaLabel: `${deltaPercentage > 0 ? '+' : deltaPercentage < 0 ? '-' : ''}${Math.abs(deltaPercentage % 1) < 0.01 ?
        Math.abs(deltaPercentage).toFixed(0) :
        Math.abs(deltaPercentage).toFixed(1)}%`,
      invertTrend,
      series: comparisonWindow.graphDates.map((date) => {
        return metricsState.metricsByDate[date]?.totals?.[counterKey] || 0;
      }),
    };
  }, [comparisonWindow.graphDates, currentCounters, metricsState.metricsByDate, previousCounters]);

  const keyMetricCards = React.useMemo(() => {
    const currentFailRate = getFailRatePercentage(currentCounters);
    const previousFailRate = getFailRatePercentage(previousCounters);
    const failRateDelta = currentFailRate !== null && previousFailRate !== null ?
      currentFailRate - previousFailRate :
      0;

    return [
      buildCountCard(translate('metrics.AgentReportCard.KeyMetrics.Marked'), 'marked', false),
      // markedGood has ridden the stored rollup since marking shipped and was
      // already being summed here — the number was in the component, unused.
      // invertTrend stays false: more passes is an improvement, and false is
      // what paints the trend chip green on a rise.
      buildCountCard(translate('metrics.AgentReportCard.KeyMetrics.Passed'), 'markedGood', false),
      {
        label: translate('metrics.AgentReportCard.KeyMetrics.FailRate'),
        currentLabel: formatRate(currentFailRate),
        previousLabel: formatRate(previousFailRate),
        delta: failRateDelta,
        deltaLabel: `${failRateDelta > 0 ? '+' : failRateDelta < 0 ? '-' : ''}${Math.abs(failRateDelta).toFixed(1)}pp`,
        invertTrend: true,
        series: comparisonWindow.graphDates.map((date) => {
          const dayCounters = metricsState.metricsByDate[date]?.totals;
          const dayFailRate = dayCounters ? getFailRatePercentage(dayCounters) : null;

          return dayFailRate === null ? 0 : dayFailRate;
        }),
      },
      buildCountCard(translate('metrics.AgentReportCard.KeyMetrics.Flagged'), 'flagged', true),
      buildCountCard(translate('metrics.AgentReportCard.KeyMetrics.AutoFails'), 'autoFailCount', true),
    ];
  }, [buildCountCard, comparisonWindow.graphDates, currentCounters, metricsState.metricsByDate, previousCounters]);

  const currentTokenSpend = React.useMemo(() => {
    return sumTokenSpendForDates(comparisonWindow.currentPeriodDates, metricsState.metricsByDate);
  }, [comparisonWindow.currentPeriodDates, metricsState.metricsByDate]);

  const tokenSpendRows = React.useMemo(() => {
    const totalSpend = emptyTokenCounters();

    Object.keys(totalSpend).forEach((counterKey) => {
      totalSpend[counterKey] = currentTokenSpend.sweep[counterKey] + currentTokenSpend.regression[counterKey];
    });

    return [
      {key: 'sweep', label: translate('metrics.AgentReportCard.TokenSpend.Sweep'), spend: currentTokenSpend.sweep},
      {key: 'regression', label: translate('metrics.AgentReportCard.TokenSpend.Regression'), spend: currentTokenSpend.regression},
      {key: 'total', label: translate('metrics.AgentReportCard.TokenSpend.Total'), spend: totalSpend, emphasize: true},
    ];
  }, [currentTokenSpend]);

  const teacherQualityCards = React.useMemo(() => ([
    buildCountCard(translate('metrics.AgentReportCard.TeacherQuality.HumanReviews'), 'humanRecords', false),
    buildCountCard(translate('metrics.AgentReportCard.TeacherQuality.Disagreements'), 'teacherHumanDisagreements', true),
    buildCountCard(translate('metrics.AgentReportCard.TeacherQuality.PassButIntervened'), 'teacherPassButIntervened', true),
    buildCountCard(translate('metrics.AgentReportCard.TeacherQuality.SkippedIntervention'), 'skippedIntervention', false),
  ]), [buildCountCard]);

  const selectedGuideRubric = React.useMemo(() => {
    if (selectedGuideRn === ALL_GUIDES_VALUE) {
      return null;
    }

    return domainGuides.find((domainGuideData) => domainGuideData.domainGuideRn === selectedGuideRn)?.rubric || null;
  }, [domainGuides, selectedGuideRn]);

  const failureCategoryRows = React.useMemo(() => {
    const categoryKeys = Array.from(
        new Set([
          ...Object.keys(currentCounters.byFailureCategory),
          ...Object.keys(previousCounters.byFailureCategory),
        ]),
    );

    return categoryKeys
        .map((categoryKey) => {
          const rubricCategory = (selectedGuideRubric?.failureCategories || [])
              .find((category) => category.key === categoryKey);
          const nowCount = currentCounters.byFailureCategory[categoryKey] || 0;
          const prevCount = previousCounters.byFailureCategory[categoryKey] || 0;

          return {
            key: categoryKey,
            label: rubricCategory?.label || prettifyCategoryKey(categoryKey),
            nowCount,
            prevCount,
            delta: nowCount - prevCount,
          };
        })
        .sort((firstRow, secondRow) => secondRow.nowCount - firstRow.nowCount);
  }, [currentCounters.byFailureCategory, previousCounters.byFailureCategory, selectedGuideRubric]);

  const missingRollupDates = React.useMemo(() => {
    if (!metricsState.hasLoadedOnce) {
      return [];
    }

    return [...comparisonWindow.currentPeriodDates, ...comparisonWindow.previousPeriodDates]
        .filter((date) => metricsState.fetchedDates[date] && !metricsState.metricsByDate[date])
        .map((date) => moment.tz(date, 'YYYY-MM-DD', timezone).format('MMM DD, YYYY'));
  }, [comparisonWindow, metricsState.fetchedDates, metricsState.hasLoadedOnce, metricsState.metricsByDate, timezone]);

  const formattedDateRange = React.useMemo(() => {
    const currentDateMoment = moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone);

    if (comparisonMode === 'DAILY') {
      return currentDateMoment.format('MMM D, YYYY');
    }

    const startDate = currentDateMoment.clone().subtract(6, 'day');

    return `${startDate.format('MMM D')} - ${currentDateMoment.format('MMM D, YYYY')}`;
  }, [comparisonMode, selectedCurrentDate, timezone]);

  const isLoading = metricsState.isLoading && !metricsState.hasLoadedOnce;

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: '#F9F8F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1160px',
          mx: 'auto',
          px: {xs: 2, md: 4},
          py: {xs: 3, md: 5.5},
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent='space-between'
            alignItems={{xs: 'flex-start', lg: 'center'}}
            spacing={2}
          >
            <Box sx={{minWidth: 0}}>
              <Typography
                sx={{
                  fontSize: {xs: '24px', md: '28px'},
                  lineHeight: '36px',
                  color: '#1A1916',
                  mb: 1,
                }}
              >
                <Translate expression='metrics.AgentReportCard.Title' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: '#A8A49E',
                }}
              >
                <Translate expression='metrics.AgentReportCard.Subtitle' />
              </Typography>
            </Box>
            <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
              <Box
                sx={{
                  'minWidth': '180px',
                  '& .MuiInputLabel-root': {
                    fontSize: '12px',
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    fontSize: '12px',
                  },
                  '& .MuiSelect-select': {
                    py: '8px',
                  },
                }}
              >
                <Dropdown
                  label={translate('metrics.AgentReportCard.GuideFilterLabel')}
                  onChange={setSelectedGuideRn}
                  value={selectedGuideRn}
                >
                  <DropdownMenuItem value={ALL_GUIDES_VALUE}>
                    {translate('metrics.AgentReportCard.AllGuides')}
                  </DropdownMenuItem>
                  {
                    domainGuides.map((domainGuideData) => (
                      <DropdownMenuItem
                        key={domainGuideData.domainGuideRn}
                        value={domainGuideData.domainGuideRn}
                      >
                        {domainGuideData.name}
                      </DropdownMenuItem>
                    ))
                  }
                </Dropdown>
              </Box>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: '#919191',
                }}
              >
                <Translate expression='metrics.AgentReportCard.VsPrior' />
              </Typography>
              <ButtonBase
                onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EBEBEB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <CalendarTodayOutlined sx={{fontSize: 16, color: '#919191'}} />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6B6761',
                  }}
                >
                  {formattedDateRange}
                </Typography>
              </ButtonBase>
              {
                datePickerAnchorEl &&
                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: '12px',
                        border: '1px solid #E8E4DE',
                        boxShadow: '0px 12px 32px rgba(26, 25, 22, 0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{p: 1}}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateCalendar
                        value={moment.tz(selectedCurrentDate, 'YYYY-MM-DD', timezone)}
                        maxDate={latestAvailableDate}
                        onChange={(nextDate) => {
                          if (!nextDate?.isValid?.()) {
                            return;
                          }

                          setSelectedCurrentDate(nextDate.clone().tz(timezone).format('YYYY-MM-DD'));
                          setDatePickerAnchorEl(null);
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Popover>
              }
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: '#EEEEEE',
                  borderRadius: '8px',
                }}
              >
                <ButtonBase
                  onClick={() => setComparisonMode('DAILY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'DAILY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'DAILY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'DAILY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.AgentReportCard.Daily' />
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  onClick={() => setComparisonMode('WEEKLY')}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: '6px',
                    backgroundColor: comparisonMode === 'WEEKLY' ? '#FFFFFF' : 'transparent',
                    boxShadow: comparisonMode === 'WEEKLY' ? '0px 4px 4px rgba(0, 0, 0, 0.05)' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 500,
                      color: comparisonMode === 'WEEKLY' ? '#000000' : '#6B6761',
                    }}
                  >
                    <Translate expression='metrics.AgentReportCard.Weekly' />
                  </Typography>
                </ButtonBase>
              </Box>
            </Stack>
          </Stack>

          <MetricCardsSection
            isLoading={isLoading}
            errorMessage={metricsState.errorMessage}
            metricCards={keyMetricCards}
            missingDates={missingRollupDates}
            sectionTitleExpression='metrics.AgentReportCard.KeyMetricsSection'
            columnCount={5}
          />

          <FailureCategoriesSection
            isLoading={isLoading}
            errorMessage={metricsState.errorMessage}
            categoryRows={failureCategoryRows}
          />

          <MetricCardsSection
            isLoading={isLoading}
            errorMessage={metricsState.errorMessage}
            metricCards={teacherQualityCards}
            missingDates={[]}
            sectionTitleExpression='metrics.AgentReportCard.TeacherQualitySection'
          />

          <TokenSpendSection
            isLoading={isLoading}
            errorMessage={metricsState.errorMessage}
            hasTokenData={currentTokenSpend.hasData}
            spendRows={tokenSpendRows}
          />
        </Stack>
      </Box>
    </Box>
  );
};

AgentReportCard.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(AgentReportCard));
